import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PageIndices, TabIndices } from '../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button
} from '@mui/material';
import { AdaptiveTabs } from '../components/common/AdaptiveTabs';
import {
    createFeelingEvent,
    updateFeelingEvent,
    createFluidEvent,
    updateFluidEvent,
    CreateFluidEventDto,
    UpdateFluidEventDto
} from '../services/calendar.service';
import { CalendarEventDto } from '../types/calendar';
import { useAuth } from '../hooks/useAuth';
import { EventEditModal } from '../components/calendar/EventEditModal';
import { CalendarsTab } from '../components/calendar/CalendarsTab';
import { SlotsTab } from '../components/calendar/SlotsTab';
import { MyCalendarTab } from '../components/calendar/MyCalendarTab';
import { AddFoodEventModal } from '../components/calendar/add-event-modals/AddFoodEventModal';
import { AddWorkoutEventModal } from '../components/calendar/add-event-modals/AddWorkoutEventModal';
import { AddMedicationEventModal } from '../components/calendar/add-event-modals/AddMedicationEventModal';
import { AddFeelingEventModal } from '../components/calendar/add-event-modals/AddFeelingEventModal';
import { AddTaskEventModal } from '../components/calendar/add-event-modals/AddTaskEventModal';
import { AddLiquidEventModal } from '../components/calendar/add-event-modals/AddLiquidEventModal';
import { useCalendarEvents } from '../hooks/useCalendarEvents';
import { saveWorkoutIntake } from '../services/workout.service';
import { createTaskEvent, updateTaskEvent, CreateTaskEventDto, UpdateTaskEventDto } from '../services/task.service';
import { saveFoodEvent } from '../services/food.service';
import { toastService } from '../services/toast.service';
import { PageHint } from '../components/common/PageHint';
import { appointmentService } from '../services/appointment.service';
import { WorkingHoursDto } from '../types/appointment.types';

const getErrorMessage = (error: any, defaultMessage: string): string => {
  if (error && error.response && error.response.data && error.response.data.message) {
    if (Array.isArray(error.response.data.message)) {
      return error.response.data.message.join(', ');
    }
    return error.response.data.message;
  }
  return defaultMessage;
};

const CALENDAR_TABS = [
  { value: 'my-calendar', label: 'Події' },
  { value: 'calendars',   label: 'Мої календарі' },
  { value: 'slots',       label: 'Слоти' },
];

const CalendarPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const {
    calendars,
    eventTypes,
    loading,
    error: calendarError,
    fetchInitialData,
    setError: setCalendarError
  } = useCalendarEvents(user);

  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [externalRefreshTrigger, setExternalRefreshTrigger] = useState(0);
  const triggerRefresh = () => setExternalRefreshTrigger(p => p + 1);

  const tabParam = searchParams.get('tab');
  const currentTab = CALENDAR_TABS.some(t => t.value === tabParam) ? tabParam! : 'my-calendar';
  const [selectedCalendarId] = useState<number | 'all'>('all');

  const [editingEvent, setEditingEvent] = useState<Partial<CalendarEventDto> | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const [openedAddModal, setOpenedAddModal] = useState<string | null>(null);
  const [feelingEventToEdit, setFeelingEventToEdit] = useState<CalendarEventDto | null>(null);
  const [liquidEventToEdit, setLiquidEventToEdit] = useState<CalendarEventDto | null>(null);
  const [medicationEventToEdit, setMedicationEventToEdit] = useState<CalendarEventDto | null>(null);
  const [workoutEventToEdit, setWorkoutEventToEdit] = useState<CalendarEventDto | null>(null);
  const [taskEventToEdit, setTaskEventToEdit] = useState<CalendarEventDto | null>(null);
  const [foodEventToEdit, setFoodEventToEdit] = useState<CalendarEventDto | null>(null);

  const [eventToDelete, setEventToDelete] = useState<CalendarEventDto | null>(null);

  const [workingHours, setWorkingHours] = useState<WorkingHoursDto[]>([]);

  useEffect(() => {
    if (user?.roles.includes('expert')) {
      appointmentService.getExpertSchedule()
        .then(setWorkingHours)
        .catch(err => console.error('Failed to fetch expert schedule', err));
    }
  }, [user]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openModalType = queryParams.get('openModal');
    if (openModalType) {
      setOpenedAddModal(openModalType);
      queryParams.delete('openModal');
      navigate({ search: queryParams.toString() }, { replace: true });
    }
  }, [location.search, navigate]);

  const handleCloseEditModal = () => {
    setEditingEvent(null);
    setIsEditModalOpen(false);
  };

  const handleAddEventSelectedFromLayout = (eventTypeCode: string) => {
    setOpenedAddModal(eventTypeCode);
  };

  const handleCloseAddModal = () => {
    setOpenedAddModal(null);
    setFeelingEventToEdit(null);
    setLiquidEventToEdit(null);
    setMedicationEventToEdit(null);
    setWorkoutEventToEdit(null);
    setTaskEventToEdit(null);
    setFoodEventToEdit(null);
  };

  const handleSaveEvent = async (_eventData: Partial<CalendarEventDto>) => {
    handleCloseEditModal();
    handleCloseAddModal();
  };

  const onSaveFeeling = useCallback(async (data: { title: string; scheduledLdt: string; isCompleted: boolean }) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if (feelingEventToEdit) {
        await updateFeelingEvent(feelingEventToEdit.id, {
          title: data.title,
          scheduledLdt: data.scheduledLdt,
          isCompleted: data.isCompleted,
        });
        toastService.success('Відчуття успішно оновлено');
      } else {
        await createFeelingEvent({
          title: data.title,
          scheduledLdt: data.scheduledLdt,
          isCompleted: data.isCompleted,
        });
        toastService.success('Відчуття успішно додано');
      }
      handleCloseAddModal();
      triggerRefresh();
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Не вдалося зберегти дані');
      setActionError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [feelingEventToEdit, handleCloseAddModal]);

  const onSaveLiquid = async (data: CreateFluidEventDto | (UpdateFluidEventDto & { id: number })) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if ('id' in data) {
        await updateFluidEvent(data.id, data);
      } else {
        await createFluidEvent(data);
      }
      handleCloseAddModal();
      triggerRefresh();
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Не вдалося зберегти дані');
      setActionError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveAndCloseAddWorkoutModal = useCallback(async (data: any) => {
    setActionLoading(true);
    setActionError(null);
    try {
      await saveWorkoutIntake(data, { eventId: workoutEventToEdit?.id });
      toastService.success(workoutEventToEdit ? 'Тренування успішно оновлено' : 'Тренування успішно додано');
      handleCloseAddModal();
      triggerRefresh();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Не вдалося зберегти дані');
      setActionError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [workoutEventToEdit, handleCloseAddModal]);

  const handleSaveAndCloseAddTaskModal = useCallback(async (data: CreateTaskEventDto | UpdateTaskEventDto) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if (taskEventToEdit) {
        await updateTaskEvent(taskEventToEdit.id, data as UpdateTaskEventDto);
        toastService.success('Задачу успішно оновлено');
      } else {
        await createTaskEvent(data as CreateTaskEventDto);
        toastService.success('Задачу успішно додано');
      }
      handleCloseAddModal();
      triggerRefresh();
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Не вдалося зберегти дані');
      setActionError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [taskEventToEdit, handleCloseAddModal]);

  const onSaveGenericEvent = useCallback(async (_data: any) => {
    handleCloseAddModal();
    triggerRefresh();
  }, [handleCloseAddModal]);

  const handleSaveFoodEvent = useCallback(async (data: {
    foodId?: number;
    customName?: string;
    name: string;
    measureKey?: string;
    quantity?: number;
    qty?: number;
    qtyG?: number;
    calendarId?: number;
    scheduledAt: Date;
    isCompleted: boolean;
    unit?: string;
    caloriesPer100g?: number;
    proteinPer100g?: number;
    fatPer100g?: number;
    carbsPer100g?: number;
    fiberPer100g?: number;
  }) => {
    try {
      await saveFoodEvent(data, { eventId: foodEventToEdit?.id });

      toastService.success(foodEventToEdit ? 'Прийом їжі успішно оновлено' : 'Прийом їжі успішно додано');
      handleCloseAddModal();
      triggerRefresh();
    } catch (error) {
      toastService.error(getErrorMessage(error, 'Не вдалося зберегти дані'));
    }
  }, [foodEventToEdit, handleCloseAddModal]);

  const handleDeleteConfirmation = async () => {
      setEventToDelete(null);
  };

  /* const handleCalendarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedCalendarId(value === 'all' ? 'all' : Number(value));
  };

  const handleCompleteEvent = async (event: CalendarEventDto, completed: boolean) => {
      try {
          setError(null);
          await completeEvent(event.id, completed);
          toastService.success('Статус події оновлено');
      } catch (err) {
          const msg = getErrorMessage(err, 'Не вдалося оновити статус події.');
          setError(msg);
          toastService.error(msg);
      }
  }; */

  const renderConfirmDeleteDialog = () => {
      if (!eventToDelete) return null;
      return (
        <Dialog
            open={true}
            onClose={() => setEventToDelete(null)}
        >
            <DialogTitle>Підтвердити видалення</DialogTitle>
            <DialogContent>
                <DialogContentText component="div">
                    Ви впевнені, що хочете видалити подію "<b>{eventToDelete.title}</b>"?
                    Цю дію неможливо буде скасувати.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setEventToDelete(null)}>Скасувати</Button>
                <Button onClick={handleDeleteConfirmation} color="error" variant="contained">
                    Видалити
                </Button>
            </DialogActions>
        </Dialog>
      );
  }

  const renderContent = () => {
    if (loading.initial) {
      return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
    }
    
    return (
        <Box data-i-doc={PageIndices.CALENDAR}>
            <PageHint iDoc={PageIndices.CALENDAR} />
            <AdaptiveTabs tabs={CALENDAR_TABS} searchParamKey="tab" defaultValue="my-calendar" />
            {(error || calendarError) && <Alert severity="error" sx={{ mt: 2 }} onClose={() => { setError(null); setCalendarError(null); }}>{error || calendarError}</Alert>}
            
            <Box data-i-doc={TabIndices.CALENDAR_MY_CALENDAR} hidden={currentTab !== 'my-calendar'}>
                <Box sx={{ p: 3 }}><MyCalendarTab calendars={calendars} eventTypes={eventTypes} userId={user?.id} externalRefreshTrigger={externalRefreshTrigger} workingHours={workingHours} /></Box>
            </Box>
            <Box data-i-doc={TabIndices.CALENDAR_CALENDARS} hidden={currentTab !== 'calendars'}>
                <Box sx={{ p: 3 }}><CalendarsTab calendars={calendars} /></Box>
            </Box>
            <Box data-i-doc={TabIndices.CALENDAR_SLOTS} hidden={currentTab !== 'slots'}>
                <Box sx={{ p: 3 }}><SlotsTab /></Box>
            </Box>
        </Box>
    )
  }

  const currentCalendarId = selectedCalendarId !== 'all' ? Number(selectedCalendarId) : undefined;

  return (
    <DashboardLayout onAddEventSelected={handleAddEventSelectedFromLayout}>
        {renderContent()}
        
        <AddFoodEventModal
          open={openedAddModal === 'food' || !!foodEventToEdit}
          onClose={handleCloseAddModal}
          onSave={handleSaveFoodEvent}
          calendarId={currentCalendarId}
          eventToEdit={foodEventToEdit}
        />
        <AddWorkoutEventModal 
          open={openedAddModal === 'workout' || !!workoutEventToEdit} 
          onClose={handleCloseAddModal} 
          onSave={handleSaveAndCloseAddWorkoutModal}
          calendarId={currentCalendarId}
          eventToEdit={workoutEventToEdit}
        />
        <AddMedicationEventModal 
          open={openedAddModal === 'medication' || !!medicationEventToEdit} 
          onClose={handleCloseAddModal} 
          onSave={onSaveGenericEvent} 
          calendarId={currentCalendarId}
          eventToEdit={medicationEventToEdit}
        />
        <AddFeelingEventModal
          open={openedAddModal === 'feeling' || !!feelingEventToEdit}
          onClose={handleCloseAddModal}
          onSave={onSaveFeeling}
          eventToEdit={feelingEventToEdit}
          loading={actionLoading}
          error={actionError}
          calendarId={currentCalendarId}
        />
        <AddTaskEventModal 
          open={openedAddModal === 'task' || !!taskEventToEdit} 
          onClose={handleCloseAddModal} 
          onSave={handleSaveAndCloseAddTaskModal}
          eventToEdit={taskEventToEdit}
          loading={actionLoading}
          error={actionError}
        />
        <AddLiquidEventModal 
            open={openedAddModal === 'fluid' || !!liquidEventToEdit} 
            onClose={handleCloseAddModal} 
            onSave={onSaveLiquid} 
            eventToEdit={liquidEventToEdit}
            calendarId={currentCalendarId}
        />

        {isEditModalOpen && (
            <EventEditModal
                open={isEditModalOpen}
                eventData={editingEvent}
                eventTypes={eventTypes}
                onClose={handleCloseEditModal}
                onSave={handleSaveEvent}
                onDelete={(_id) => {
                    handleCloseEditModal();
                }}
            />
        )}

        {renderConfirmDeleteDialog()}
    </DashboardLayout>
  );
};

export default CalendarPage;
